/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { jsonWebKeySetsRetireKey } from "../funcs/jsonWebKeySetsRetireKey.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { JSONWebKey } from "../models/components/jsonwebkey.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  RetireJsonWebKeyRequest,
  RetireJsonWebKeySecurity,
} from "../models/operations/retirejsonwebkey.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type RetireJsonWebKeyMutationVariables = {
  request: RetireJsonWebKeyRequest;
  security?: RetireJsonWebKeySecurity | undefined;
  options?: RequestOptions;
};

export type RetireJsonWebKeyMutationData = JSONWebKey;

export type RetireJsonWebKeyMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * retireKey jsonWebKeySets
 *
 * @remarks
 * Take the set's active key out of signing use without withdrawing it: the key stays published so tokens already signed with it keep verifying. The key must be active. This is the graceful wind-down; use revoke when the key must stop verifying too. Requires org:admin.
 */
export function useRetireJsonWebKeyMutation(
  options?: MutationHookOptions<
    RetireJsonWebKeyMutationData,
    RetireJsonWebKeyMutationError,
    RetireJsonWebKeyMutationVariables
  >,
): UseMutationResult<
  RetireJsonWebKeyMutationData,
  RetireJsonWebKeyMutationError,
  RetireJsonWebKeyMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildRetireJsonWebKeyMutation(client, options),
    ...options,
  });
}

export function mutationKeyRetireJsonWebKey(): MutationKey {
  return ["@gram/client", "jsonWebKeySets", "retireKey"];
}

export function buildRetireJsonWebKeyMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: RetireJsonWebKeyMutationVariables,
  ) => Promise<RetireJsonWebKeyMutationData>;
} {
  return {
    mutationKey: mutationKeyRetireJsonWebKey(),
    mutationFn: function retireJsonWebKeyMutationFn({
      request,
      security,
      options,
    }): Promise<RetireJsonWebKeyMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(jsonWebKeySetsRetireKey(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
